import { Link } from "wouter";
import { Layout } from "@/components/Layout";
import { ArrowRight, Target, Layers, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/use-seo";

export default function Home() {
  useSEO({
    title: "The Lifecycle Canvas - Map Your Interim Executive Journey",
    description: "Interactive planning tool for Interim Executives to visualize career transitions, identify pain points, and plan interventions across your professional lifecycle.",
    ogTitle: "The Lifecycle Canvas - Map Your Interim Executive Journey",
    ogDescription: "Interactive planning tool for Interim Executives to visualize career transitions and plan interventions.",
    twitterTitle: "The Lifecycle Canvas",
    twitterDescription: "Interactive planning tool for Interim Executives.",
  });

  return (
    <Layout>
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 opacity-30">
          <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none">
            <path
              d="M0,300 C150,100 300,500 450,450 C600,200 750,550 900,400 C1050,150 1200,300 1200,300"
              fill="none"
              stroke="url(#hero-gradient)"
              strokeWidth="3"
            />
            <defs>
              <linearGradient id="hero-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2dd4bf" />
                <stop offset="50%" stopColor="#f43f5e" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6 leading-tight">
            Map Your <span className="text-teal-400">Interim Executive</span> Lifecycle
          </h1>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Visualize your career transitions, identify pain points, and strategically plan interventions across on-assignment and off-assignment phases.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/apply">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-600 text-white px-8 py-6 text-lg" data-testid="cta-early-access">
                Apply for Early Access
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link href="/about">
              <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-6 text-lg" data-testid="cta-learn-more">
                About the Framework
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-sm text-slate-400">
            Already approved?{" "}
            <Link
              href="/sign-in"
              className="font-medium text-teal-300 transition-colors hover:text-teal-200"
              data-testid="cta-sign-in"
            >
              Sign in
            </Link>
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">
            Plan Your Professional Journey
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-teal-500/20 text-teal-400 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Lifecycle Modes</h3>
              <p className="text-slate-400 text-sm">
                Map your journey through Market Activation, Strategic Readiness, Impact Delivery, and more.
              </p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-rose-500/20 text-rose-400 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Pain Points</h3>
              <p className="text-slate-400 text-sm">
                Identify challenges like Pipeline Anxiety, Identity Drift, and Cashflow Gaps before they derail you.
              </p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-amber-500/20 text-amber-400 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Interventions</h3>
              <p className="text-slate-400 text-sm">
                Plan strategic actions from LinkedIn audits to networking events across your timeline.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-display font-bold text-white mb-6">
            Ready to Take Control?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Request access to the interactive canvas for mapping your interim executive lifecycle.
          </p>
          <Link href="/apply">
            <Button size="lg" className="bg-teal-700 hover:bg-teal-600 text-white px-8" data-testid="cta-bottom-early-access">
              Request Early Access
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
